import { parseBag, RAW_RESOURCES, type RawResourceId, type ResourceBag } from './resources.js';

export const CONTINENTS = ['asia', 'europe', 'africa', 'north-america', 'south-america', 'oceania'] as const;
export type ContinentId = (typeof CONTINENTS)[number];

/** Ordem de prioridade do sorteio de capitais (maior continente primeiro). */
export const CONTINENT_DRAW_ORDER: readonly ContinentId[] = CONTINENTS;

export const CONTINENT_NAMES: Record<ContinentId, string> = {
  asia: 'Ásia',
  europe: 'Europa',
  africa: 'África',
  'north-america': 'América do Norte',
  'south-america': 'América do Sul',
  oceania: 'Oceania',
};

export interface TerritoryDef {
  id: string;
  /** Nome curto (mapa, notícias). */
  name: string;
  /** Nome com detalhes entre parênteses, quando houver. */
  fullName: string;
  continent: ContinentId;
  coastal: boolean;
  /** Valor fiscal: impostos por turno quando consolidado. */
  taxValue: number;
  /** Matéria-prima por turno quando consolidado. */
  production: Partial<Record<RawResourceId, number>>;
}

function t(
  id: string,
  fullName: string,
  continent: ContinentId,
  coastal: boolean,
  taxValue: number,
  production: string,
): TerritoryDef {
  const name = fullName.replace(/\s*\(.*\)\s*$/, '');
  return {
    id,
    name,
    fullName,
    continent,
    coastal,
    taxValue,
    production: parseBag(production) as TerritoryDef['production'],
  };
}

const NA = 'north-america' as const;
const SA = 'south-america' as const;
const EU = 'europe' as const;
const AF = 'africa' as const;
const AS = 'asia' as const;
const OC = 'oceania' as const;

/** Territórios terrestres (TERRITORIOS.md). */
export const TERRITORIES: readonly TerritoryDef[] = [
  // América do Norte (18)
  t('alasca', 'Alasca', NA, true, 15, 'PET 30 · MAD 15 · FER 5'),
  t('canada-oeste', 'Canadá Oeste', NA, true, 30, 'PET 35 · MAD 30 · FER 10'),
  t('canada-central', 'Canadá Central', NA, true, 20, 'ALI 35 · PET 10 · URA 5'),
  t('canada-leste', 'Canadá Leste', NA, true, 40, 'MAD 30 · FER 20 · ALI 15 · URA 3'),
  t('groenlandia', 'Groenlândia', NA, true, 5, 'FER 10 · ALI 5'),
  t('eua-noroeste', 'EUA Noroeste', NA, true, 35, 'MAD 30 · ALI 20 · BAU 5'),
  t('eua-california', 'EUA Califórnia', NA, true, 60, 'ALI 35 · SIL 20 · PET 15'),
  t('eua-sudoeste', 'EUA Sudoeste', NA, false, 25, 'FER 15 · SIL 10 · URA 5'),
  t('eua-montanhas', 'EUA Montanhas', NA, false, 20, 'FER 20 · PET 15 · URA 4'),
  t('eua-grandes-planicies', 'EUA Grandes Planícies', NA, false, 20, 'ALI 40 · PET 10'),
  t('eua-texas', 'EUA Texas', NA, true, 45, 'PET 45 · ALI 20'),
  t('eua-centro-oeste', 'EUA Centro-Oeste', NA, false, 45, 'ALI 35 · FER 20'),
  t('eua-sudeste', 'EUA Sudeste', NA, true, 40, 'ALI 25 · MAD 25 · BAU 5'),
  t('eua-nordeste', 'EUA Nordeste', NA, true, 60, 'FER 15 · SIL 10 · ALI 10'),
  t('mexico-norte', 'México Norte', NA, true, 25, 'FER 20 · PET 15 · ALI 15'),
  t('mexico-sul', 'México Sul', NA, true, 25, 'PET 25 · ALI 20 · MAD 10'),
  t('america-central', 'América Central', NA, true, 15, 'ALI 25 · MAD 20'),
  t('caribe', 'Caribe', NA, true, 15, 'ALI 20 · BAU 10'),
  // América do Sul (16)
  t('colombia', 'Colômbia', SA, true, 25, 'PET 20 · ALI 20 · FER 10'),
  t('venezuela', 'Venezuela', SA, true, 25, 'PET 45 · FER 15 · BAU 10'),
  t('guianas', 'Guianas', SA, true, 10, 'BAU 25 · MAD 20'),
  t('equador', 'Equador', SA, true, 15, 'PET 20 · ALI 15'),
  t('peru', 'Peru', SA, true, 20, 'FER 30 · ALI 10'),
  t('bolivia', 'Bolívia', SA, false, 10, 'FER 15 · PET 10 · SIL 10'),
  t('chile', 'Chile', SA, true, 30, 'FER 35 · ALI 10'),
  t('paraguai', 'Paraguai', SA, false, 10, 'ALI 25 · MAD 15'),
  t('uruguai', 'Uruguai', SA, true, 15, 'ALI 25'),
  t('argentina-norte', 'Argentina Norte', SA, true, 30, 'ALI 30 · PET 10'),
  t('argentina-sul', 'Argentina Sul', SA, true, 15, 'PET 25 · ALI 10'),
  t('brasil-norte', 'Brasil Norte', SA, true, 15, 'MAD 45 · BAU 25 · FER 10'),
  t('brasil-nordeste', 'Brasil Nordeste', SA, true, 25, 'ALI 20 · PET 10 · SIL 5'),
  t('brasil-centro-oeste', 'Brasil Centro-Oeste', SA, false, 25, 'ALI 45 · FER 5'),
  t('brasil-sudeste', 'Brasil Sudeste', SA, true, 55, 'FER 40 · PET 30 · ALI 15 · SIL 10'),
  t('brasil-sul', 'Brasil Sul', SA, true, 35, 'ALI 35 · MAD 25'),
  // Europa (23)
  t('islandia', 'Islândia', EU, true, 10, 'ALI 10'),
  t('irlanda', 'Irlanda', EU, true, 25, 'ALI 20'),
  t('reino-unido', 'Reino Unido', EU, true, 55, 'PET 25 · FER 15 · ALI 10'),
  t('noruega', 'Noruega', EU, true, 35, 'PET 35 · MAD 15 · ALI 10'),
  t('suecia', 'Suécia', EU, true, 35, 'FER 35 · MAD 25 · URA 3'),
  t('finlandia', 'Finlândia', EU, true, 25, 'MAD 35 · FER 10'),
  t('portugal', 'Portugal', EU, true, 20, 'ALI 15 · MAD 10 · FER 5'),
  t('espanha', 'Espanha', EU, true, 40, 'ALI 25 · FER 15'),
  t('franca', 'França', EU, true, 55, 'ALI 35 · BAU 10 · URA 3'),
  t('benelux', 'Benelux', EU, true, 50, 'ALI 20 · SIL 10 · PET 5'),
  t('alemanha', 'Alemanha', EU, true, 60, 'FER 30 · ALI 15 · SIL 10'),
  t('alpes', 'Alpes (Suíça e Áustria)', EU, false, 45, 'MAD 15 · ALI 10 · FER 5'),
  t('italia', 'Itália', EU, true, 45, 'ALI 25 · BAU 5'),
  t('polonia', 'Polônia', EU, true, 30, 'FER 25 · ALI 25'),
  t(
    'europa-central',
    'Europa Central (Tchéquia, Eslováquia, Hungria)',
    EU,
    false,
    30,
    'FER 15 · BAU 15 · ALI 15 · URA 3',
  ),
  t('balcas', 'Bálcãs', EU, true, 20, 'BAU 15 · FER 10 · ALI 10 · MAD 10'),
  t('grecia', 'Grécia', EU, true, 20, 'BAU 10 · ALI 10'),
  t('romenia', 'Romênia e Moldávia', EU, true, 20, 'PET 20 · ALI 20 · MAD 10'),
  t('balticos', 'Bálticos', EU, true, 20, 'MAD 20 · ALI 15'),
  t('belarus', 'Belarus', EU, false, 15, 'ALI 20 · MAD 15'),
  t('ucrania', 'Ucrânia', EU, true, 25, 'ALI 40 · FER 30 · URA 3'),
  t('russia-ocidental', 'Rússia Ocidental', EU, true, 45, 'MAD 20 · ALI 20 · FER 10'),
  t('russia-sul', 'Rússia Sul', EU, true, 25, 'PET 30 · ALI 30'),
  // África (23)
  t('marrocos', 'Marrocos', AF, true, 20, 'ALI 15 · FER 10'),
  t('argelia', 'Argélia', AF, true, 25, 'PET 35 · FER 10'),
  t('tunisia', 'Tunísia', AF, true, 15, 'ALI 10 · PET 10'),
  t('libia', 'Líbia', AF, true, 20, 'PET 40'),
  t('egito', 'Egito', AF, true, 30, 'ALI 25 · PET 15'),
  t('sudao', 'Sudão', AF, true, 10, 'PET 15 · ALI 15'),
  t('etiopia', 'Etiópia e Eritreia', AF, true, 15, 'ALI 25'),
  t('chifre-da-africa', 'Chifre da África (Somália, Djibuti)', AF, true, 5, 'ALI 10'),
  t('sahel-ocidental', 'Sahel Ocidental (Mauritânia, Mali, Níger)', AF, true, 10, 'FER 20 · URA 8'),
  t('chade', 'Chade', AF, false, 5, 'PET 10 · ALI 5'),
  t('africa-ocidental', 'África Ocidental (Senegal a Serra Leoa)', AF, true, 15, 'BAU 30 · ALI 15'),
  t('golfo-da-guine', 'Golfo da Guiné (Libéria a Benin)', AF, true, 20, 'ALI 25 · MAD 15'),
  t('nigeria', 'Nigéria', AF, true, 30, 'PET 45 · ALI 20'),
  t('camaroes', 'Camarões e República Centro-Africana', AF, true, 10, 'MAD 25 · PET 10'),
  t('gabao', 'Gabão e Guiné Equatorial', AF, true, 15, 'PET 25 · MAD 25 · URA 3'),
  t('congo', 'Congo', AF, true, 15, 'MAD 30 · FER 25 · URA 5'),
  t('africa-oriental', 'África Oriental (Quênia, Uganda, Tanzânia)', AF, true, 25, 'ALI 30 · MAD 10'),
  t('angola', 'Angola', AF, true, 20, 'PET 35 · MAD 10'),
  t('zambia', 'Zâmbia, Zimbábue e Malawi', AF, false, 15, 'FER 25 · ALI 15'),
  t('mocambique', 'Moçambique', AF, true, 10, 'ALI 15 · MAD 15 · PET 5 · BAU 5'),
  t('namibia', 'Namíbia e Botsuana', AF, true, 15, 'FER 10 · URA 8'),
  t('africa-do-sul', 'África do Sul', AF, true, 40, 'FER 30 · ALI 15 · URA 4'),
  t('madagascar', 'Madagascar', AF, true, 10, 'ALI 15 · MAD 15'),
  // Ásia (35)
  t('turquia', 'Turquia', AS, true, 40, 'ALI 25 · FER 15'),
  t('caucaso', 'Cáucaso (Geórgia, Armênia, Azerbaijão)', AS, true, 15, 'PET 25 · ALI 10'),
  t('levante', 'Levante (Síria, Líbano, Israel, Palestina, Jordânia)', AS, true, 35, 'ALI 15 · SIL 10'),
  t('iraque', 'Iraque', AS, true, 25, 'PET 45 · ALI 10'),
  t('arabia-saudita', 'Arábia Saudita', AS, true, 40, 'PET 50'),
  t('iemen-oma', 'Iêmen e Omã', AS, true, 15, 'PET 15 · ALI 5'),
  t('estados-do-golfo', 'Estados do Golfo (Kuwait, Catar, Emirados, Bahrein)', AS, true, 55, 'PET 45'),
  t('ira', 'Irã', AS, true, 30, 'PET 40 · FER 15 · URA 3'),
  t('cazaquistao', 'Cazaquistão', AS, false, 25, 'PET 20 · FER 15 · ALI 15 · URA 10'),
  t(
    'asia-central',
    'Ásia Central (Uzbequistão, Turcomenistão, Quirguistão, Tajiquistão)',
    AS,
    false,
    15,
    'ALI 15 · PET 10 · URA 4',
  ),
  t('afeganistao', 'Afeganistão', AS, false, 5, 'FER 10 · ALI 10'),
  t('paquistao', 'Paquistão', AS, true, 25, 'ALI 25 · FER 5'),
  t('india-norte', 'Índia Norte (com Nepal e Butão)', AS, false, 35, 'ALI 40'),
  t('india-sul', 'Índia Sul (com Sri Lanka)', AS, true, 40, 'ALI 25 · FER 25 · SIL 10'),
  t('india-leste', 'Índia Leste e Bangladesh', AS, true, 30, 'ALI 35 · FER 10'),
  t('russia-urais', 'Rússia Urais', AS, false, 30, 'FER 35 · PET 15 · BAU 5'),
  t('siberia-ocidental', 'Sibéria Ocidental', AS, true, 25, 'PET 50 · MAD 20'),
  t('siberia-oriental', 'Sibéria Oriental', AS, true, 10, 'MAD 40 · FER 15 · URA 4'),
  t('extremo-oriente-russo', 'Extremo Oriente Russo', AS, true, 15, 'MAD 30 · PET 15 · ALI 10'),
  t('mongolia', 'Mongólia', AS, false, 10, 'FER 20 · ALI 10 · URA 3'),
  t('china-nordeste', 'China Nordeste (Manchúria)', AS, true, 35, 'ALI 30 · PET 15 · FER 15'),
  t('china-norte', 'China Norte', AS, true, 55, 'FER 35 · ALI 15'),
  t('china-oeste', 'China Oeste (Xinjiang e Tibete)', AS, false, 15, 'PET 20 · FER 10 · URA 3'),
  t('china-leste', 'China Leste', AS, true, 60, 'ALI 35 · SIL 20'),
  t('china-sul', 'China Sul', AS, true, 50, 'ALI 25 · SIL 20 · BAU 15'),
  t('coreia-do-norte', 'Coreia do Norte', AS, true, 10, 'FER 15 · MAD 10'),
  t('coreia-do-sul', 'Coreia do Sul', AS, true, 50, 'SIL 30 · ALI 10'),
  t('japao', 'Japão', AS, true, 60, 'SIL 25 · ALI 15'),
  t('taiwan', 'Taiwan', AS, true, 45, 'SIL 40'),
  t('indochina', 'Indochina (Vietnã, Laos, Camboja)', AS, true, 25, 'ALI 30 · BAU 10 · PET 10'),
  t('tailandia-mianmar', 'Tailândia e Mianmar', AS, true, 30, 'ALI 25 · MAD 20'),
  t('malasia-singapura', 'Malásia e Singapura', AS, true, 45, 'PET 15 · SIL 15 · ALI 10'),
  t('indonesia-oeste', 'Indonésia Oeste (Sumatra, Java)', AS, true, 35, 'ALI 30 · PET 20 · MAD 15'),
  t(
    'indonesia-leste',
    'Indonésia Leste (Bornéu, Sulawesi, Papua Ocidental)',
    AS,
    true,
    15,
    'MAD 35 · BAU 15 · FER 10',
  ),
  t('filipinas', 'Filipinas', AS, true, 25, 'ALI 20 · FER 10'),
  // Oceania (8)
  t('australia-ocidental', 'Austrália Ocidental', OC, true, 25, 'FER 45 · BAU 20'),
  t('australia-norte', 'Austrália Norte', OC, true, 10, 'BAU 15 · URA 10'),
  t('australia-sul', 'Austrália Sul', OC, true, 20, 'ALI 20 · URA 8'),
  t('australia-leste', 'Austrália Leste', OC, true, 50, 'ALI 25 · FER 20'),
  t('nova-zelandia', 'Nova Zelândia', OC, true, 30, 'ALI 25 · MAD 15'),
  t('papua-nova-guine', 'Papua-Nova Guiné', OC, true, 10, 'MAD 25 · FER 10 · PET 10'),
  t('pacifico-sul', 'Pacífico Sul (Fiji, Nova Caledônia, Vanuatu, Samoa)', OC, true, 10, 'FER 10 · ALI 10'),
  t('havai', 'Havaí', OC, true, 25, 'ALI 10'),
];

export const TERRITORY_BY_ID: Readonly<Record<string, TerritoryDef>> = Object.fromEntries(
  TERRITORIES.map((def) => [def.id, def]),
);

export const TERRITORY_IDS: readonly string[] = TERRITORIES.map((def) => def.id);

export function isTerritoryId(id: string): boolean {
  return id in TERRITORY_BY_ID;
}

export function totalProduction(def: TerritoryDef): number {
  let total = 0;
  for (const qty of Object.values(def.production)) total += qty ?? 0;
  return total;
}

/** Guarnição neutra: 2 + (produção total ÷ 20) + (valor fiscal ÷ 20), arredondada (mediana 6). */
export function neutralGarrison(def: TerritoryDef): number {
  return Math.round(2 + totalProduction(def) / 20 + def.taxValue / 20);
}

/** Guarnição exigida = guarnição neutra ÷ 2 (mínimo 3 soldados). */
export function requiredGarrison(def: TerritoryDef): number {
  return Math.max(3, Math.round(neutralGarrison(def) / 2));
}

/** Elegível como capital inicial: valor fiscal ≥ 20, ≥ 2 tipos de matéria-prima, ALI ≥ 15. */
export function isCapitalEligible(def: TerritoryDef): boolean {
  const kinds = RAW_RESOURCES.filter((r) => (def.production[r] ?? 0) > 0).length;
  return def.taxValue >= 20 && kinds >= 2 && (def.production.food ?? 0) >= 15;
}

export function productionBag(def: TerritoryDef): ResourceBag {
  return { ...def.production };
}
